import * as React from 'react';

// scrollWidth/clientWidth are integers, but line-heights are not: a 13px font
// on a 16.25px line-height reports scrollHeight 17 vs clientHeight 16, so the
// vertical check fired on EVERY single-line span and the tooltip opened whether
// or not anything was clipped. One pixel of slack absorbs that rounding.
//
// Safe on both axes: a line-clamped block overflows by at least a full line
// (~16px+), and horizontally-truncated text overflows by far more than 1px
// before the ellipsis is even visible.
const ROUNDING_SLACK = 1;

/**
 * True once the returned ref's element is actually clipped by its own box —
 * measured via scrollWidth vs. clientWidth, re-checked on resize (column
 * widths change with the sidebar/breakpoint/scroll). `key` re-runs the
 * measurement when the underlying content changes (pass the display text
 * itself, or a stable string derived from whatever content the element
 * renders — e.g. a joined list of names).
 *
 * Callers use this to gate a tooltip so it only opens for content that
 * doesn't fully fit its box, instead of firing on every hover regardless of
 * overflow. Works for a single truncated text span, a fixed-width single-line
 * row of other elements (e.g. badges), or a `-webkit-line-clamp`'d multi-line
 * block — checks both axes since line-clamped text overflows vertically
 * (scrollHeight > clientHeight) rather than horizontally.
 *
 * NOTE: never mount/unmount a wrapper around the measured element based on
 * the returned boolean (`isTruncated ? <Tooltip>{el}</Tooltip> : el`) — the
 * element-type change remounts the node, and since this effect is keyed on
 * `key` (content), the ResizeObserver keeps watching the detached original
 * and misses every later resize. Keep the tree shape constant and gate the
 * tooltip's `open` prop instead — see components/common/TruncationTooltip.
 */
export function useIsTruncated<T extends HTMLElement = HTMLElement>(
  key: string,
): [React.RefObject<T | null>, boolean] {
  const [isTruncated, setIsTruncated] = React.useState(false);
  const ref = React.useRef<T | null>(null);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () =>
      setIsTruncated(
        el.scrollWidth > el.clientWidth + ROUNDING_SLACK ||
          el.scrollHeight > el.clientHeight + ROUNDING_SLACK,
      );
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [key]);

  return [ref, isTruncated];
}
